import images from '@assets/images/index'
import { Button } from '@components/ui'
import { removeSearchParams } from '@utils/assessmentReportUtils'
import { getLocale } from '@utils/getLocale'
import {
  Col,
  Divider,
  Flex,
  MenuProps,
  Upload,
  UploadProps,
  message
} from 'antd'
import { useState } from 'react'

import { Link } from '@suntex.core/nav'
import {
  Entities_Response_Team_Player,
  LoginId,
  SchoolId,
  TeamId,
  UserId
} from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $SpecialGradeLevel,
  Locale,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework } from '@suntex.fim/framework'
import { useAppQueryString } from '@suntex.fim/nav'
import {
  useTeamInfo,
  useTeamRosterQuery,
  useTeamSummaryQuery
} from '@suntex.fim/team'
import {
  Dropdown,
  Modal,
  SearchInput,
  Tooltip,
  showErrorMessage
} from '@suntex.fim/ui'

import AddEditStudentForm from './Forms/AddEditStudentForm'
import { getAlternateCredentialsError } from './alternateCredentials'
import EditTeamForm from './Forms/EditTeamForm'
import TeamSummary from './TeamSummary'
import TeamRosterTable from './tables/TeamRosterTable'

function TeamRoster() {
  const { api } = useFramework()
  const { noneUserId } = useAppQueryString()
  const { login } = useAuth(isAuthenticated)
  const teamInfo = useTeamInfo()
  const [searchQuery, setSearchQuery] = useState(noneUserId || '')
  const [isMutating, setIsMutating] = useState(false)
  const [AddEditStudentModal, setAddEditStudentModal] = useState(false)
  const [AddMultiStudentModal, setAddMultiStudentModal] = useState(false)
  const [EditTeamModal, setEditTeamModal] = useState(false)

  const teamId = teamInfo?.id ?? (0 as TeamId)
  const hasTeamId = !!teamInfo?.id

  const {
    data: rosterData,
    isLoading: isRosterLoading,
    refetch: refetchRoster
  } = useTeamRosterQuery(teamId, { enabled: hasTeamId })

  const {
    data: summaryData,
    isLoading: isSummaryLoading,
    refetch: refetchSummary
  } = useTeamSummaryQuery(teamId, { enabled: hasTeamId })

  const data: Entities_Response_Team_Player[] = rosterData?.players ?? []
  const remainingLicenses = rosterData?.remainingLicenses ?? 0
  const totalLicenses = rosterData?.totalLicenses ?? 0
  const isLoading = isRosterLoading || isSummaryLoading || isMutating

  const [formType, setFormType] = useState<'add' | 'edit'>('add')
  const [grade, setGrade] = useState<SpecialGradeLevel>(
    SpecialGradeLevel.Kindergarten
  )
  const [TLName, setTLName] = useState('')
  const [className, setClassName] = useState('')
  const [userId, setUserId] = useState('' as UserId)
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [id, setId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [altLogin, setAltLogin] = useState('' as UserId)
  const [altPassword, setAltPassword] = useState('')
  const [loginId, setLoginId] = useState(0 as LoginId)
  const [isIdInUse, setIsIdInUse] = useState<boolean>(false)
  const [isCredentialInUse, setIsCredentialInUse] = useState<boolean>(false)
  /**
   * Reset the form and set all field to initial value
   */
  const resetForm = () => {
    setUserId('' as UserId)
    setPassword('')
    setDisplayName('')
    setId('')
    setFirstName('')
    setLastName('')
    setAltLogin('' as UserId)
    setAltPassword('')
    setLoginId(0 as LoginId)
    setFormType('add')
  }

  /**
   * Define menu items for dropdown
   */
  const items: MenuProps['items'] = [
    {
      key: '1',
      label: 'Add a Student',
      onClick: () => setAddEditStudentModal(true)
    },
    {
      key: '2',
      label: (
        <Link
          to={`/team/${teamInfo?.id}/team-management/add-multiple-students`}
          className="text-body"
        >
          Add Multiple Students
        </Link>
      )
    }
  ]

  // dragger
  const { Dragger } = Upload

  const props: UploadProps = {
    name: 'file',
    multiple: false,
    onChange(info: {
      file: { name?: unknown; status?: unknown }
      fileList: unknown
    }) {
      const { status } = info.file
      if (status !== 'uploading') {
        // eslint-disable-next-line no-console
        console.log(info.file, info.fileList)
      }
      if (status === 'done') {
        message.success(`${info.file.name} file uploaded successfully.`)
      } else if (status === 'error') {
        message.error(`${info.file.name} file upload failed.`)
        message.error('API pending')
      }
    },
    onDrop(e) {
      // eslint-disable-next-line no-console
      console.log('Dropped files', e.dataTransfer.files)
    }
  }

  const openEditTeamModal = () => {
    if (summaryData) {
      setGrade($SpecialGradeLevel.from(summaryData.info.grade))
      setTLName(summaryData.info.displayName || '')
      setClassName(summaryData.info.className || '')
    }
    setEditTeamModal(true)
  }

  /**
   * handles edit team and updates data in state and store
   */
  const handleEditTeam = async () => {
    if (className.length > 0) {
      try {
        await api.put_Team_Update({
          teamId: teamInfo?.id || (0 as TeamId),
          // 0 keeps the team in its current school; the edit form never moves it
          schoolId: 0 as SchoolId,
          displayName: TLName,
          className,
          excludeRankings: true,
          grade
        })
        await refetchSummary()
        message.success('Team updated successfully!')
        setEditTeamModal(false)
      } catch (error) {
        message.error('Unable to edit team.')
      }
    } else {
      message.error('Please Enter Class Name!')
    }
  }
  // function to handle add and edit student and reset the form
  const handleAddEditStudent = async (type: 'add' | 'edit') => {
    //  call API if altLogin/altPassword combination and id number is not in use
    if (!isIdInUse && !isCredentialInUse) {
      const alternateCredentialsError = getAlternateCredentialsError(
        altLogin,
        altPassword
      )
      if (alternateCredentialsError) {
        message.error(alternateCredentialsError)
        return
      }
      let modifiedDisplayName = displayName.replaceAll(' ', '')
      // check if display name is specified
      if (modifiedDisplayName.length === 0) {
        // For US maxlength is 6 and for rest of the countries maxlength is 16
        const locale = getLocale()
        if (firstName.length > 0 || lastName.length > 0) {
          if (locale === Locale.US) {
            modifiedDisplayName =
              firstName.substring(0, 5) + lastName.substring(0, 1)
          } else {
            modifiedDisplayName =
              firstName.substring(0, 15) + lastName.substring(0, 1)
          }
        } else {
          message.error('Please Enter Display Name!!')
          return
        }
      }
      // call API to add or edit student
      try {
        if (type === 'add') {
          await api.post_Team_CreateMultiplePlayers({
            players: [
              {
                teamId: teamInfo!.id,
                displayName: modifiedDisplayName,
                firstName,
                lastName,
                idNumber: id || undefined,
                altUser: altLogin || undefined,
                altPassword: altPassword || undefined,
                classificationGroup: undefined
              }
            ]
          })
        } else {
          await api.put_Team_UpdatePlayer({
            loginId,
            displayName,
            firstName,
            lastName,
            idNumber: id || undefined,
            altUser: altLogin || undefined,
            altPassword: altPassword || undefined,
            classificationGroup: undefined
          })
        }
        refetchRoster()
        message.success(
          `Student ${formType === 'add' ? 'added' : 'updated'} successfully!`
        )
        resetForm()
        setAddEditStudentModal(false)
        setSearchQuery('')
        if (noneUserId) removeSearchParams('noneUserId')
      } catch (error) {
        showErrorMessage('Unable to add student', error)
      }
    } else {
      message.error(
        'This id number is already in use by another player in your school district or this altLogin/altPassword combination is already in use by another player'
      )
    }
  }

  return (
    <Flex vertical gap="20px">
      <Flex className="main-navigation" align="center" justify="space-between">
        <Col>
          <Flex gap="10px" className="main-navigation__heading" align="center">
            <h3>Team Roster</h3>
          </Flex>
        </Col>
        <Col>
          <Flex justify="space-between" align="center">
            <Button
              intent="primary"
              icon={<images.EditWhite />}
              iconPosition="end"
              onClick={openEditTeamModal}
            >
              Edit Team
            </Button>
            {/* Edit team modal starts */}
            <Modal
              closable={false}
              centered
              title="Edit Team"
              open={EditTeamModal}
              onOk={() => setEditTeamModal(false)}
              onCancel={() => setEditTeamModal(false)}
              className="add-player"
              width="626px"
              footer={[
                <>
                  <Button
                    shape="round"
                    intent="secondary"
                    key="back"
                    onClick={() => setEditTeamModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    shape="round"
                    intent="primary"
                    onClick={handleEditTeam}
                    disabled={!TLName}
                    style={{
                      cursor: !TLName ? 'not-allowed' : 'pointer'
                    }}
                  >
                    Save Changes
                  </Button>
                </>
              ]}
            >
              <EditTeamForm
                summaryData={summaryData}
                grade={grade}
                setGrade={(grade) => $SpecialGradeLevel.from(grade)}
                TLName={TLName}
                setTLName={setTLName}
                className={className}
                setClassName={setClassName}
              />
            </Modal>
            {/* Edit team modal ends */}
          </Flex>
        </Col>
      </Flex>
      <Flex className="tiles-container" wrap="wrap">
        <Flex className="left" vertical gap="20px" flex="1 1 560px">
          <TeamSummary
            isLoading={isLoading}
            summaryRecordData={summaryData}
            remainingLicenses={remainingLicenses}
            totalLicenses={totalLicenses}
          />
        </Flex>
      </Flex>
      {/* result */}
      <div className="main-result">
        {/* Updated toolbar design */}
        <Flex
          justify="space-between"
          align="center"
          className="main-result__toolbar"
          wrap
        >
          <h4 className="title">Team Players</h4>

          <Flex gap="10px" wrap>
            <div className="report-search">
              <SearchInput value={searchQuery} onChange={setSearchQuery} />
            </div>
            <Dropdown
              label="Add Students"
              ariaLabel="Add students options"
              menu={{ items }}
              className={
                remainingLicenses < 0 ? 'disabled tertiary' : 'tertiary'
              }
              disabled={remainingLicenses < 0}
              icon={
                <images.UserCircleAdd aria-hidden="true" focusable="false" />
              }
            />
            {/* Add Students modal starts */}
            <Modal
              closable={false}
              centered
              title={
                <Flex gap={8} align="center">
                  {`${formType === 'add' ? 'Add' : 'Edit'} a Student`}{' '}
                  <span>
                    <Tooltip
                      arrow={false}
                      placement="topLeft"
                      classNames={{ root: 'progress-tooltip' }}
                      title="Please enter a display name.However, if you enter first name and last name, we will compute the display name for you.You can override the display name at anytime."
                    >
                      <images.InfoCircle />
                    </Tooltip>
                  </span>
                </Flex>
              }
              open={AddEditStudentModal}
              onOk={() => setAddEditStudentModal(false)}
              onCancel={() => setAddEditStudentModal(false)}
              className="add-player"
              width="626px"
              footer={[
                <>
                  <Button
                    shape="round"
                    intent="secondary"
                    key="back"
                    onClick={() => {
                      setAddEditStudentModal(false)
                      resetForm()
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    shape="round"
                    intent="tertiary"
                    key="submit"
                    disabled={isIdInUse || isCredentialInUse}
                    onClick={() =>
                      handleAddEditStudent(formType === 'add' ? 'add' : 'edit')
                    }
                  >
                    {`${formType === 'add' ? 'Add' : 'Edit'} Student`}
                  </Button>
                </>
              ]}
            >
              <AddEditStudentForm
                type={formType}
                userId={userId}
                password={password}
                displayName={displayName}
                setDisplayName={setDisplayName}
                id={id}
                setId={setId}
                firstName={firstName}
                setFirstName={setFirstName}
                lastName={lastName}
                setLastName={setLastName}
                altLogin={altLogin}
                setAltLogin={setAltLogin}
                altPassword={altPassword}
                setAltPassword={setAltPassword}
                setIsIdInUse={setIsIdInUse}
                setIsCredentialInUse={setIsCredentialInUse}
                loginId={loginId}
                setPassword={setPassword}
              />
            </Modal>
            {/* Add Students modal ends */}
            {/* Add Multiple Students modal starts */}
            <Modal
              closable={false}
              centered
              title={
                <>
                  <h4>Add Multiple Students</h4>
                  <div className="text-sm text-body leading-1">
                    Add Multiple records by uploading excel/csv files.{' '}
                  </div>
                </>
              }
              open={AddMultiStudentModal}
              onOk={() => setAddMultiStudentModal(false)}
              onCancel={() => setAddMultiStudentModal(false)}
              className="add-player"
              width="626px"
              footer={[
                <>
                  <Button
                    shape="round"
                    intent="secondary"
                    key="back"
                    onClick={() => {
                      setAddMultiStudentModal(false)
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    shape="round"
                    intent="tertiary"
                    key="submit"
                    onClick={() => {
                      setAddMultiStudentModal(false)
                    }}
                  >
                    &apos;Upload File&apos;
                  </Button>
                </>
              ]}
            >
              <>
                <Dragger {...props}>
                  <p className="ant-upload-text leading-1">Drag & Drop Files</p>
                  <p className="ant-upload-text leading-1">or</p>
                  <p className="ant-upload-text leading-1">
                    Click to Browse files.
                  </p>
                </Dragger>
                <p>
                  .csv, .xlsx files supported <Divider type="vertical" />
                  <a
                    href="/add-students-sample.xlsx"
                    download="add-students-sample"
                  >
                    Download example file
                  </a>
                </p>
              </>

              <TeamRosterTable
                globalFilter={searchQuery}
                data={[...data]}
                showModal={() => null}
                isLoading={isLoading}
                graphColor="#3FB2FF"
                setFormType={setFormType}
                setDisplayName={setDisplayName}
                setUserId={setUserId}
                setPassword={setPassword}
                setId={setId}
                setFirstName={setFirstName}
                setLastName={setLastName}
                setAltLogin={setAltLogin}
                setAltPassword={setAltPassword}
                setLoginId={setLoginId}
                setAddEditStudentModal={setAddEditStudentModal}
                getRosters={() => refetchRoster()}
                setIsLoading={setIsMutating}
              />
            </Modal>
            {/* Add Multiple Students modal ends */}
          </Flex>
        </Flex>
        <TeamRosterTable
          globalFilter={searchQuery}
          data={[...data]}
          showModal={() => null}
          isLoading={isLoading}
          graphColor="#3FB2FF"
          setFormType={setFormType}
          setDisplayName={setDisplayName}
          setUserId={setUserId}
          setPassword={setPassword}
          setId={setId}
          setFirstName={setFirstName}
          setLastName={setLastName}
          setAltLogin={setAltLogin}
          setAltPassword={setAltPassword}
          setLoginId={setLoginId}
          setAddEditStudentModal={setAddEditStudentModal}
          getRosters={() => refetchRoster()}
          setIsLoading={setIsMutating}
        />
      </div>
    </Flex>
  )
}

export default TeamRoster
